import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import * as ScenarioAPI from '../../../services/ScenarioServices';
import * as CanvasAPI from '../../../services/CanvasServices';
import { useNavigate, useParams } from 'react-router-dom';
import AuthContext from '../../../context/AuthContext';
import ScenarioTitlePageTags from './ScenarioTitlePageTags';
import ImageCropModal from './ImageCropModal';
import styles from './styles/ScenarioTitlePage.module.css';
import TextEditor from '../../texteditor/TextEditor';
import 'ckeditor5/ckeditor5.css';
import ScenarioInviteModal from './ScenarioInviteModal';
import { FaMinus } from 'react-icons/fa';
import profileIcon from "../../../images/profileicon.png";
import { TUTORIAL_TARGETS, tutorialAttr, useTutorialLock } from '../../../tutorials';

// Mirrors the server's cap in addCollaborator. Kept here so the button can say
// so up front rather than the author discovering it from a silent no-op.
const COLLABORATOR_CAP = 2;

const ScenarioTitlePage = () => {
    const navigate = useNavigate();
    const { scenarioId } = useParams();
    const { userId, user } = useContext(AuthContext);

    const [scenario, setScenario] = useState({ title: '', description: '', image: null });
    const [tags, setTags] = useState([]);
    const [isPrivate, setIsPrivate] = useState(false);
    const [showMap, setShowMap] = useState(false);
    // The stored password is a hash the server never returns, so this field starts
    // empty and is only submitted once the author actually types in it — otherwise
    // saving the settings page would silently clear an existing password.
    const [password, setPassword] = useState('');
    const [hasPassword, setHasPassword] = useState(false);
    const [passwordTouched, setPasswordTouched] = useState(false);
    const [isHidden, setIsHidden] = useState(false);
    const [isLogged, setIsLogged] = useState(false);
    const [initialData, setInitialData] = useState('');
    const [originalIsLogged, setOriginalIsLogged] = useState(false);
    const [sharedUser, setSharedUser] = useState([]);
    const [creatorId, setCreatorId] = useState(null);
    const [collaboratorError, setCollaboratorError] = useState(null);
    const [pendingRemoval, setPendingRemoval] = useState(null);
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [cropImageSrc, setCropImageSrc] = useState(null);

    // The tutorial walks through these settings one at a time and has decided
    // what each should be, so the controls are shown but inert.
    const locked = useTutorialLock('titlePage');

    // Only the creator (or a site admin) may change who else can edit — that is
    // what the add/remove routes enforce with verifyCreator. A collaborator can
    // open this page, so the controls have to be absent for them rather than
    // present and 403-ing.
    const canManageCollaborators =
        (creatorId && String(creatorId) === String(userId)) || user?.tier >= 2;

    const editorRef = useRef(null);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: useCallback((acceptedFiles) => {
            if (acceptedFiles.length) {
                const fileUrl = URL.createObjectURL(acceptedFiles[0]);
                setCropImageSrc(fileUrl);
            }
        }, []),
    });
    
    const fetchScenarioData = useCallback(async () => {
        try {
            const data = await ScenarioAPI.getScenario(scenarioId, 'sharedUser,tags');

            if (data) {
                // Everything on this page is a scenario setting, and settings are
                // the creator's alone — PATCH /api/scenarios/:id enforces that. A
                // collaborator who reaches the URL is sent to the canvas rather
                // than shown a form whose Save button can only ever 403.
                const creator = data.creator?._id || data.creator || null;
                if (creator && String(creator) !== String(userId) && !(user?.tier >= 2)) {
                    navigate(`/create/${scenarioId}/canvas`, { replace: true });
                    return;
                }

                setScenario({
                    title: data.title || '',
                    description: data.description || '',
                    image: data.image || null,
                });
                setIsPrivate(data.isPrivate || false);
                setHasPassword(!!data.hasPassword);
                setIsHidden(data.isHidden || false);
                setIsLogged(data.isLogged || false);
                setShowMap(data.showMap || false);
                setTags(data.tags || []);
                setInitialData(data.description);
                // Populated by the `sharedUser` populate above; an unpopulated or
                // absent value would break the list rendering below.
                setSharedUser(Array.isArray(data.sharedUser) ? data.sharedUser : []);
                setCreatorId(creator);
                setOriginalIsLogged(data.isLogged || false);

                // The `isEditing` write that used to be here is gone. `isEditing`
                // is not in updateScenario's allowed-fields whitelist and nothing
                // reads it, so the request only ever bumped `lastUpdated` — one
                // round trip and a spurious "edited just now" every time anybody
                // opened this page.
            }
        } catch (error) {
            console.error('Error fetching scenario data:', error);
            if (error.response?.status === 403) {
                navigate('/home');
            } else {
                alert('An error occurred while verifying ownership.');
            }
        }
    }, [scenarioId, userId, user?.tier, navigate]);

    useEffect(() => {
        fetchScenarioData();
    }, [fetchScenarioData]);

    useEffect(() => {
        if (editorRef.current) {
            editorRef.current.setData(initialData);
        }
    }, [initialData]);

    const handleInputChange = useCallback((event) => {
        const { name, value } = event.target;
        setScenario((prev) => ({ ...prev, [name]: value }));
    }, []);

    const handleCheckboxChange = useCallback(() => {
        setIsPrivate((prev) => {
            if (prev) setPassword('');
            return !prev;
        });
    }, []);

    const handleHiddenChange = useCallback(() => {
        setIsHidden((prev) => !prev);
    }, []);

    const handleLoggedChange = useCallback(() => {
        setIsLogged((prev) => !prev);
    }, []);

    const handleShowMapChange = useCallback(() => {
        setShowMap((prev) => !prev);
    }, []);

    const handleSubmit = useCallback(async () => {
        try {
            const formData = new FormData();
            formData.append('title', scenario.title);
            formData.append('description', scenario.description);
            formData.append('isPrivate', isPrivate);
            // Send the password only when it was edited, or when protection was
            // turned off (which clears it).
            if (passwordTouched) formData.append('password', password);
            else if (!isPrivate && hasPassword) formData.append('password', '');
            formData.append('isHidden', isHidden);
            formData.append('isLogged', isLogged);
            formData.append('showMap', showMap);
            if (scenario.image) formData.append('image', scenario.image);

            await ScenarioAPI.updateScenario(scenarioId, formData);

            // Turning logging on or off for the scenario sets it on every situation.
            // That is an edit to the DRAFT, like any other authoring change — it
            // reaches players at the next publish, not immediately. One op batch,
            // so a slow scenario is one request rather than one per situation.
            if (isLogged !== originalIsLogged) {
                const canvas = await CanvasAPI.getCanvas(scenarioId);
                const ops = (canvas?.nodes || []).map((node) => ({
                    type: 'updateNodeData',
                    id: node.id,
                    data: { isLogged },
                }));
                if (ops.length > 0) await CanvasAPI.sendOps(scenarioId, ops);
            }

            navigate(`/create/${scenarioId}/canvas`);
        } catch (error) {
            console.error('Error updating scenario:', error);
        }
    }, [scenario, tags, isPrivate, password, passwordTouched, hasPassword, isHidden, isLogged, showMap, scenarioId, userId, originalIsLogged, navigate]);

    const deleteImageFromServer = useCallback(async () => {
        try {
            await ScenarioAPI.updateScenario(scenarioId, { image: null });
            setScenario((prev) => ({ ...prev, image: null }));
        } catch (error) {
            console.error('Error deleting image:', error);
        }
    }, [scenarioId]);

    const handleTextEditorChange = (data) => {
        setScenario((prev) => ({
            ...prev,
            description: data
        }));
    };

    // The response carries the updated list, so removal is one round trip and the
    // rendered list is always what the server actually stores.
    const handleRemoveCollaborator = useCallback(async (collaboratorId) => {
        setCollaboratorError(null);
        setPendingRemoval(collaboratorId);
        try {
            const { collaborators } = await ScenarioAPI.removeCollaborator(scenarioId, collaboratorId);
            setSharedUser(collaborators || []);
        } catch (error) {
            console.error('Error removing collaborator:', error);
            setCollaboratorError(
                error.response?.status === 403
                    ? 'Only the scenario creator can change the collaborator list.'
                    : 'Could not remove that collaborator. Please try again.'
            );
        } finally {
            setPendingRemoval(null);
        }
    }, [scenarioId]);


    return (
        <div className={styles.pageContainer}>
            <h1 className={styles.pageTitle}>Edit Your Title Page</h1>
            <form className={styles.form} onSubmit={(e) => e.preventDefault()}>
                <div className={styles.formField}>
                    <div
                        id="scenarioImageUpload"
                        {...getRootProps()}
                        className={`${styles.imageDropzone} ${scenario.image ? styles.hasImage : ''}`}
                        tabIndex={0}
                    >
                        <input {...getInputProps()} />
                        {scenario.image ? (
                            <>
                                <div className={styles.imagePreviewContainer}>
                                    <img
                                        className={styles.imagePreview}
                                        src={
                                            typeof scenario.image === 'string'
                                                ? scenario.image
                                                : URL.createObjectURL(scenario.image)
                                        }
                                        alt="Scenario"
                                    />
                                </div>
                                <div className={styles.imageOverlay}>
                                    <span className={styles.imageOverlayText}>Click to change image</span>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            deleteImageFromServer();
                                        }}
                                        className={`${styles.buttonIcon} ${styles.removeImageButton}`}
                                        aria-label="Remove image"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className={styles.dropzonePlaceholder}>
                                <svg xmlns="http://www.w3.org/2000/svg" className={styles.uploadIcon} viewBox="0 0 24 24" fill="currentColor"><path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/></svg>
                                <p className={styles.dropzoneText}>Drag & drop image or click to upload</p>
                                <p className={styles.dropzoneHint}>(Max 5MB)</p>
                            </div>
                        )}
                    </div>
                </div>
    
                <div className={styles.formField} {...tutorialAttr(TUTORIAL_TARGETS.TITLE_TITLE)}>
                    <label className={styles.label} htmlFor="scenarioTitle">Title</label>
                    <input
                        id="scenarioTitle"
                        type="text"
                        name="title"
                        autoFocus={!locked}
                        value={scenario.title}
                        onChange={handleInputChange}
                        className={styles.inputField}
                        maxLength={60}
                        placeholder="Enter scenario title"
                        readOnly={locked}
                    />
                    <div className={styles.charCount}>{60 - scenario.title.length} characters remaining</div>
                </div>
    
                <div className={styles.formField} {...tutorialAttr(TUTORIAL_TARGETS.TITLE_DESCRIPTION)}>
                    <label className={styles.label} htmlFor="scenarioDescriptionEditor">Description</label>
                    <div className={styles.textEditorWrapper} id="scenarioDescriptionEditor">
                        <TextEditor
                            initialData={initialData}
                            onChange={handleTextEditorChange}
                            readOnly={locked}
                        />
                    </div>
                </div>
    
                <div className={styles.formField}>
                    <h2 className={styles.subheading}>Settings</h2>
                    <div className={styles.checkboxItem} {...tutorialAttr(TUTORIAL_TARGETS.TITLE_PASSWORD)}>
                        <label className={styles.checkboxLabel} htmlFor="isPrivateCheckbox">
                            <input
                                id="isPrivateCheckbox"
                                type="checkbox"
                                checked={isPrivate}
                                onChange={handleCheckboxChange}
                                className={styles.checkboxInput}
                                disabled={locked}
                            />
                            <span className={styles.checkboxCustom}></span>
                            <span className={styles.checkboxText}>Add a Password</span>
                        </label>
                        {isPrivate && (
                            <div className={styles.passwordFieldContainer}>
                                <label className={styles.labelNested} htmlFor="scenarioPassword">Enter a password</label>
                                <input
                                    id="scenarioPassword"
                                    type="text"
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        setPasswordTouched(true);
                                    }}
                                    className={styles.inputField}
                                    placeholder={
                                        hasPassword && !passwordTouched
                                            ? 'Password set — type to change it'
                                            : 'Enter password'
                                    }
                                />
                            </div>
                        )}
                    </div>
                    <div className={styles.checkboxItem} {...tutorialAttr(TUTORIAL_TARGETS.TITLE_HIDDEN)}>
                        <label className={styles.checkboxLabel} htmlFor="isHiddenCheckbox">
                            <input
                                id="isHiddenCheckbox"
                                type="checkbox"
                                checked={isHidden}
                                onChange={handleHiddenChange}
                                className={styles.checkboxInput}
                                disabled={locked}
                            />
                            <span className={styles.checkboxCustom}></span>
                            <span className={styles.checkboxText}>Hide From Public</span>
                        </label>
                    </div>
                    {/* Normally a paid feature, but the tour explains what it is
                        regardless — a free-tier reader should know it exists. */}
                    {(user.tier > 0 || locked) && <div className={styles.checkboxItem} {...tutorialAttr(TUTORIAL_TARGETS.TITLE_LOGS)}>
                        <label className={styles.checkboxLabel} htmlFor="isLoggedCheckbox">
                            <input
                                id="isLoggedCheckbox"
                                type="checkbox"
                                checked={isLogged}
                                onChange={handleLoggedChange}
                                className={styles.checkboxInput}
                                disabled={locked}
                            />
                            <span className={styles.checkboxCustom}></span>
                            <span className={styles.checkboxText}>Create Logs</span>
                        </label>
                    </div> }
                     <div className={styles.checkboxItem} {...tutorialAttr(TUTORIAL_TARGETS.TITLE_MAP)}>
                        <label className={styles.checkboxLabel} htmlFor="showMapCheckbox">
                            <input
                                id="showMapCheckbox"
                                type="checkbox"
                                checked={showMap}
                                onChange={handleShowMapChange}
                                className={styles.checkboxInput}
                                disabled={locked}
                            />
                            <span className={styles.checkboxCustom}></span>
                            <span className={styles.checkboxText}>Display Map</span>
                        </label>
                    </div>
                </div>

                <div className={styles.formField} {...tutorialAttr(TUTORIAL_TARGETS.TITLE_COLLABORATORS)}>
                    <h2 className={styles.subheading}>Collaborators</h2>
                    <p className={styles.collaboratorHint}>
                        Collaborators can edit this scenario alongside you. Up to {COLLABORATOR_CAP}.
                    </p>

                    {sharedUser.length === 0 ? (
                        <p className={styles.collaboratorEmpty}>No collaborators yet.</p>
                    ) : (
                        sharedUser.map((collaborator) => (
                            <div key={collaborator._id} className={styles.collaboratorItem}>
                                <img
                                    src={collaborator.profilePicture || profileIcon}
                                    alt=""
                                    className={styles.profileIcon}
                                />
                                <span className={styles.userInfo}>
                                    {collaborator.username}
                                    {collaborator.email ? ` (${collaborator.email})` : ''}
                                </span>
                                {canManageCollaborators && (
                                    <button
                                        type="button"
                                        className={styles.removeButton}
                                        onClick={() => handleRemoveCollaborator(collaborator._id)}
                                        disabled={pendingRemoval === collaborator._id}
                                        aria-label={`Remove ${collaborator.username}`}
                                    >
                                        <FaMinus />
                                    </button>
                                )}
                            </div>
                        ))
                    )}

                    {collaboratorError && (
                        <p className={styles.collaboratorError}>{collaboratorError}</p>
                    )}

                    {canManageCollaborators && (
                        <button
                            type="button"
                            className={`${styles.button} ${styles.secondaryButton} ${styles.addCollaboratorButton}`}
                            onClick={() => setIsInviteModalOpen(true)}
                            disabled={locked || sharedUser.length >= COLLABORATOR_CAP}
                        >
                            {sharedUser.length >= COLLABORATOR_CAP
                                ? 'Collaborator limit reached'
                                : 'Add collaborator'}
                        </button>
                    )}
                </div>

    
                <div className={styles.formField}>
                    <div id="scenarioTags" {...tutorialAttr(TUTORIAL_TARGETS.TITLE_TAGS)}>
                        <ScenarioTitlePageTags
                                tags={tags}
                                setTags={setTags}
                                scenarioId={scenarioId}
                                userId={userId}
                                locked={locked}
                            />
                    </div>
                </div>
    
                <div className={styles.formActions}>
                    <button type="button" className={`${styles.button} ${styles.secondaryButton}`} onClick={() => navigate('/home')}>
                        Home
                    </button>
                    <button
                        type="button"
                        className={`${styles.button} ${styles.primaryButton}`}
                        onClick={handleSubmit}
                        {...tutorialAttr(TUTORIAL_TARGETS.TITLE_NEXT)}
                    >
                        Next
                    </button>
                </div>

                {isInviteModalOpen && (
                    <ScenarioInviteModal
                        isOpen={isInviteModalOpen}
                        onClose={() => setIsInviteModalOpen(false)}
                        scenarioId={scenarioId}
                        currentCollaborators={sharedUser}
                        setCollaborators={setSharedUser}
                        creatorId={creatorId}
                        cap={COLLABORATOR_CAP}
                    />
                )}

                {cropImageSrc && (
                    <ImageCropModal
                        imageSrc={cropImageSrc}
                        onCropComplete={(croppedFile) => {
                            setScenario((prev) => ({ ...prev, image: croppedFile }));
                            URL.revokeObjectURL(cropImageSrc);
                            setCropImageSrc(null);
                        }}
                        onCancel={() => {
                            URL.revokeObjectURL(cropImageSrc);
                            setCropImageSrc(null);
                        }}
                    />
                )}
            </form>
        </div>
    );
    
};

export default React.memo(ScenarioTitlePage);
